import type { ProgressDashboard, ReflectionQuota, SocialGoal } from "@/types";

/**
 * The last dashboard payload this process fetched, so reopening it paints
 * instead of spinning.
 *
 * `/dashboard` is a ROUTE, not a mounted tab, so navigating to it builds the
 * screen from scratch every single time — and the screen holds its whole
 * content behind one spinner until three requests land. Unlike the tab screens,
 * there is no "revisits are instant" consolation: measured on device, every
 * open paid the full wait.
 *
 * In memory only, and deliberately not persisted to disk. This is a person's
 * goals, reflections and activity history; it lives exactly as long as the
 * process and no longer, which is the same rule the chat message cache follows.
 *
 * Keyed by user + range + reset marker, because all three change what the
 * numbers mean:
 *
 * - **user** — the next account to sign in shares this process. Sign-out clears
 *   the cache outright (below), and the key is a second lock on the same door.
 * - **range** — 7 and 30 are different questions with different answers. Only
 *   `progress` actually differs between them; `goals` and `quota` describe the
 *   account rather than the window, which is why a range switch reads a cached
 *   entry for its chart alone and never for the rest.
 * - **resetAt** — "reset my metrics" moves the floor every number is counted
 *   from, so a payload read before it describes a world that no longer exists.
 *
 * There is no TTL. Freshness is not this cache's job: every reader refetches in
 * the background on open, so what a cached payload buys is the FIRST paint, and
 * it is replaced seconds later by the same request that would otherwise have
 * been blocking. A stale-by-a-minute number that is visibly refreshing beats an
 * empty screen that is honest about knowing nothing.
 */
export type DashboardSnapshot = {
  progress: ProgressDashboard;
  goals: SocialGoal[];
  quota: ReflectionQuota;
};

/**
 * One GENERATION at a time — a user and a reset marker — holding one entry per
 * range.
 *
 * This was a single slot keyed by all three, which looked range-aware and was
 * not: writing Month evicted Week, so flipping back re-fetched and the "both
 * ranges stay warm" behaviour never existed. Verified by the test below, which
 * writes 7, writes 30, and reads 7 back.
 *
 * Bounded by construction rather than by an eviction policy: `range` is the
 * literal union `7 | 30`, so the map holds at most two entries, and a write
 * from a different user or after a metrics reset clears it rather than adding
 * to it. There is nothing here that can grow.
 */
type Generation = string;

function generationOf(
  userId: string | null | undefined,
  resetAt: string | null,
): Generation {
  return `${userId ?? "anon"}|${resetAt ?? "none"}`;
}

let generation: Generation | null = null;
const entries = new Map<7 | 30, DashboardSnapshot>();

export function readDashboardCache(
  userId: string | null | undefined,
  range: 7 | 30,
  resetAt: string | null,
): DashboardSnapshot | null {
  if (generation !== generationOf(userId, resetAt)) return null;
  return entries.get(range) ?? null;
}

export function writeDashboardCache(
  userId: string | null | undefined,
  range: 7 | 30,
  resetAt: string | null,
  snapshot: DashboardSnapshot,
): void {
  const next = generationOf(userId, resetAt);
  // A different account, or the same account after a reset, invalidates every
  // range at once — the numbers are counted from a floor that just moved.
  if (generation !== next) {
    entries.clear();
    generation = next;
  }
  entries.set(range, snapshot);
}

/**
 * Emptied by sign-out, called from `resetSpaceState` beside
 * `clearChatMessageCache()` — the same pattern, for the same reason: this is
 * one account's private history sitting in a process the next account to sign
 * in on this device will share. The generation already scopes by user, but that
 * only protects the account that signs in NEXT; this protects the one that just
 * left.
 *
 * Imports nothing on purpose, so it can be driven by a real test under node —
 * `chat-message-cache.ts` is kept loadable for exactly the same reason.
 */
export function clearDashboardCache(): void {
  entries.clear();
  generation = null;
}
